import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { ConfigProvider, theme as antdTheme } from "antd";
import zhCN from "antd/lib/locale/zh_CN";
import App from "./App";
import "./styles/index.css";
import { APP_INFO } from "./config/appConfig";

// 设置页面标题和 favicon（根据环境动态设置）
document.title = APP_INFO.FULL_NAME;

// 动态切换 favicon 以区分环境（蓝=生产, 绿=DEV, 橙=S1, 紫=S2, 青=S3）
if (APP_INFO.FAVICON) {
    document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']").forEach(link => {
        link.href = APP_INFO.FAVICON + "?v=" + Date.now();
    });
}


// 抑制 ResizeObserver 错误（这是浏览器的已知问题，不影响功能）
const resizeObserverErrHandler = (e) => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message === 'ResizeObserver loop limit exceeded') {
        e.stopImmediatePropagation();
        return;
    }
};
window.addEventListener('error', resizeObserverErrHandler);

const LEGACY_SW_CLEANUP_RELOAD_KEY = "fm_legacy_sw_cleanup_reloaded";

const clearLegacyServiceWorkers = async () => {
    if (process.env.NODE_ENV !== "development" || !("serviceWorker" in navigator)) {
        return false;
    }

    try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const sameOriginRegistrations = registrations.filter((registration) => {
            try {
                return new URL(registration.scope).origin === window.location.origin;
            } catch {
                return false;
            }
        });

        if (sameOriginRegistrations.length === 0) {
            sessionStorage.removeItem(LEGACY_SW_CLEANUP_RELOAD_KEY);
            return false;
        }

        await Promise.all(sameOriginRegistrations.map((registration) => registration.unregister()));

        if ("caches" in window) {
            const cacheNames = await window.caches.keys();
            await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
        }

        if (navigator.serviceWorker.controller && !sessionStorage.getItem(LEGACY_SW_CLEANUP_RELOAD_KEY)) {
            sessionStorage.setItem(LEGACY_SW_CLEANUP_RELOAD_KEY, "1");
            window.location.reload();
            return true;
        }

        sessionStorage.removeItem(LEGACY_SW_CLEANUP_RELOAD_KEY);
        return false;
    } catch (error) {
        console.warn("Failed to clear legacy service workers", error);
        return false;
    }
};

// 创建QueryClient实例
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            staleTime: 60000,
        },
    },
});

// React Router future flags 设置
const routerFutureConfig = {
    // v7_startTransition 已禁用：该 flag 将路由状态更新包装在 React.startTransition 中，
    // 导致页面有进行中的异步状态更新（如数据请求）时，导航 transition 被反复打断无法提交，
    // 表现为 URL 变了但页面不切换。等升级到 React Router 7 后用其原生方案替代。
    v7_startTransition: false,
    v7_relativeSplatPath: true,
};

const flymetaAntdTokenBridge = {
    colorPrimary: "var(--fm-raw-primary)",
    colorError: "var(--fm-raw-error)",
    colorSuccess: "var(--fm-raw-status-ontime)",
    colorWarning: "var(--fm-raw-status-delayed)",
    colorInfo: "var(--fm-raw-status-info)",
    colorBgLayout: "var(--fm-raw-background)",
    colorText: "var(--fm-raw-foreground)",
};

const antdLegacyColorSeeds = {
    colorPrimary: "#1677ff",
    colorError: "#ff4d4f",
    colorSuccess: "#52c41a",
    colorWarning: "#faad14",
    colorInfo: "#1890ff",
};

// AntD 会先派生色板再输出 CSS 变量；派生阶段使用旧端浅色种子，避免 var()/OKLCH 被解析为黑色。
const flymetaThemeAlgorithm = (seedToken) => ({
    ...antdTheme.defaultAlgorithm({
        ...seedToken,
        ...antdLegacyColorSeeds,
    }),
    ...flymetaAntdTokenBridge,
});

// 全局配置 Ant Design
const antdConfig = {
    locale: zhCN,
    theme: {
        cssVar: true,
        algorithm: flymetaThemeAlgorithm,
        token: {
            ...flymetaAntdTokenBridge,
            borderRadius: 4,
        },
        components: {
            Table: {
                colorBgContainer: "#ffffff",
                rowHoverBg: "#91d5ff", // 普通行悬停背景色： #91d5ff (对应 Ant Design 调色板的 blue-3)
                rowSelectedHoverBg: "#69c0ff", // 选中行悬停背景色： #69c0ff (对应 Ant Design 调色板的 blue-4)
            },
            Select: {
                optionActiveBg: "#91d5ff", // 下拉项悬停背景色： #91d5ff (与表格行悬停色一致)
            },
            Layout: {
                headerBg: "#fff",
            },
        },
    },
    components: {
        TimePicker: {
            needConfirm: false,
            onKeyDown: (e) => {
                if (e.target.value && e.target.value.length === 2 && e.key !== "Backspace" && e.key !== ":") {
                    e.target.value += ":";
                }
            },
        },
    },
};

const renderApp = () => {
    const root = ReactDOM.createRoot(document.getElementById("root"));

    root.render(
        <React.StrictMode>
            <BrowserRouter future={routerFutureConfig}>
                <QueryClientProvider client={queryClient}>
                    <ConfigProvider {...antdConfig}>
                        <App />
                    </ConfigProvider>
                    <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
                </QueryClientProvider>
            </BrowserRouter>
        </React.StrictMode>,
    );
};

clearLegacyServiceWorkers().then((reloadTriggered) => {
    if (!reloadTriggered) {
        renderApp();
    }
});
